import { CONTROL_GROUP_FOCUS_CLASS } from "../lib/controlStyles";
import {
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Command as CommandPrimitive } from "cmdk";
import { Check, Search, SlidersHorizontal, X } from "lucide-react";

import { cn } from "../lib/cn";
import { Command, CommandGroup, CommandItem, CommandList } from "./Command";
import { Popover, PopoverAnchor, PopoverContent } from "./Popover";

export interface SearchFilterBarOption {
  value: string;
  label: string;
  /** One short line under the label, in list facets. */
  description?: string;
  icon?: ElementType;
  /** How many records carry the value. */
  count?: number;
  /**
   * Colour of the value. In a list facet it tints a small dot; in a chip facet
   * and in the active filter it styles the chip itself (e.g. `tag-chip` with
   * `tagStyle(tag)`), so a tag looks the same here as everywhere else.
   */
  swatchClassName?: string;
  swatchStyle?: CSSProperties;
}

export interface SearchFilterBarFacet {
  id: string;
  /** Heading of the facet in the panel, and prefix of its active filter. */
  label: string;
  options: readonly SearchFilterBarOption[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  /** Several values at once (default). A single-choice facet replaces its value. */
  multiple?: boolean;
  /**
   * `"list"` (default): one row per value, with its icon and description — for
   * a few meaningful values. `"chips"`: a wrapping cloud of compact chips — for
   * many short values such as tags, so a long list never takes over the panel.
   */
  display?: "list" | "chips";
}

export interface SearchFilterBarProps {
  /** Accessible name of the search box, e.g. "Search dashboards". */
  label: string;
  query: string;
  onQueryChange: (query: string) => void;
  placeholder?: string;
  facets?: readonly SearchFilterBarFacet[];
  /** Trailing status, e.g. "8 of 13". Announced politely when it changes. */
  resultSummary?: ReactNode;
  className?: string;
}

function matches(option: SearchFilterBarOption, query: string) {
  return option.label.toLocaleLowerCase().includes(query);
}

/**
 * One search field that can also filter by facets (tags, type, status…).
 *
 * The field stays a plain search: focusing it opens nothing. A quiet "Filter"
 * command at its end opens one compact panel, a row of chips per facet (label on
 * the left, values with their counts on the right). Typing a word that names a
 * value also offers it, so a tag can be picked from the keyboard. Active
 * filters sit inside the field as removable chips, so the page never grows a row
 * of badges. A facet with a single value filters nothing and is left out.
 * Keyboard: Down opens the panel, arrows move, Enter toggles a value,
 * Backspace in an empty field removes the last filter, Escape closes.
 *
 * Generic on purpose: it only knows values, labels and counts, so any list
 * view can use it without a filter bar of its own.
 */
export function SearchFilterBar({
  label,
  query,
  onQueryChange,
  placeholder = "Search",
  facets: allFacets = [],
  resultSummary,
  className,
}: SearchFilterBarProps) {
  const [open, setOpen] = useState(false);
  const [openedByTyping, setOpenedByTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const hintId = useId();
  const panelId = useId();

  const facets = allFacets.filter(
    (facet) => facet.options.length > 1 || facet.selected.length > 0,
  );
  const normalized = query.trim().toLocaleLowerCase();
  const active = allFacets.flatMap((facet) =>
    facet.selected
      .map((value) => facet.options.find((option) => option.value === value))
      .filter((option): option is SearchFilterBarOption => Boolean(option))
      .map((option) => ({ facet, option })),
  );
  const hasActive = active.length > 0 || query.length > 0;
  const groups = facets
    .map((facet) => ({
      facet,
      options: normalized ? facet.options.filter((o) => matches(o, normalized)) : facet.options,
    }))
    .filter((group) => group.options.length > 0);
  const panelOpen = open && groups.length > 0;

  function changeOpen(next: boolean, byTyping = false) {
    setOpen(next);
    setOpenedByTyping(next && byTyping);
  }

  function toggle(facet: SearchFilterBarFacet, value: string) {
    const checked = facet.selected.includes(value);
    if (facet.multiple === false) facet.onChange(checked ? [] : [value]);
    else
      facet.onChange(
        checked ? facet.selected.filter((item) => item !== value) : [...facet.selected, value],
      );
    // A value found by typing has done its job: clear the text so the list is not filtered twice.
    if (query) {
      onQueryChange("");
      if (openedByTyping) changeOpen(false);
    }
  }

  function clearAll() {
    onQueryChange("");
    for (const facet of allFacets) if (facet.selected.length > 0) facet.onChange([]);
    inputRef.current?.focus();
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace" && query === "" && active.length > 0) {
      const last = active[active.length - 1];
      last.facet.onChange(last.facet.selected.filter((value) => value !== last.option.value));
    } else if (event.key === "Escape") {
      if (panelOpen) {
        event.preventDefault();
        changeOpen(false);
      } else if (query) {
        onQueryChange("");
      }
    } else if (event.key === "ArrowDown" && !open && facets.length > 0) {
      changeOpen(true);
    }
  }

  return (
    <Command label={label} shouldFilter={false} loop className="contents">
      <Popover open={panelOpen} onOpenChange={(next) => changeOpen(next)}>
        <PopoverAnchor asChild>
          <div
            ref={anchorRef}
            onMouseDown={(event) => {
              // Anywhere in the field (padding, icon, gaps) puts the caret in the input.
              if (
                event.target === event.currentTarget ||
                !(event.target as Element).closest("button, input")
              ) {
                event.preventDefault();
                inputRef.current?.focus();
              }
            }}
            className={cn(
              "flex min-h-10 w-full cursor-text flex-wrap items-center gap-1.5 rounded-control border border-control-border bg-surface py-1 pl-3 pr-1 transition-[border-color,box-shadow]",
              "hover:border-control-border-hover",
              CONTROL_GROUP_FOCUS_CLASS,
              className,
            )}
          >
            <Search className="size-4 shrink-0 text-muted" aria-hidden />
            {active.map(({ facet, option }) => (
              <ActiveFilter
                key={`${facet.id}:${option.value}`}
                facet={facet}
                option={option}
                onRemove={() => {
                  facet.onChange(facet.selected.filter((value) => value !== option.value));
                  inputRef.current?.focus();
                }}
              />
            ))}
            <CommandPrimitive.Input
              ref={inputRef}
              value={query}
              onValueChange={(value) => {
                onQueryChange(value);
                if (value.trim()) {
                  if (!open) changeOpen(true, true);
                } else if (openedByTyping) changeOpen(false);
              }}
              onKeyDown={onInputKeyDown}
              aria-label={label}
              aria-describedby={hintId}
              placeholder={active.length > 0 ? "Search within these filters" : placeholder}
              className="h-8 min-w-[12rem] flex-1 bg-transparent px-1 text-sm text-primary outline-none placeholder:text-muted"
            />
            <span id={hintId} className="sr-only">
              {facets.length > 0
                ? "Press Down to choose filters. Backspace removes the last filter."
                : ""}
            </span>
            <span className="ml-auto flex shrink-0 items-center gap-0.5">
              {resultSummary !== undefined && (
                <span
                  role="status"
                  aria-live="polite"
                  className="px-1.5 text-xs tabular-nums text-muted"
                >
                  {resultSummary}
                </span>
              )}
              {hasActive ? (
                <button
                  type="button"
                  onClick={clearAll}
                  aria-label="Clear search and filters"
                  title="Clear search and filters"
                  className="touch-target grid size-8 place-items-center rounded-md text-muted transition-colors hover:bg-surface-elevated hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  <X className="size-4" aria-hidden />
                </button>
              ) : null}
              {facets.length > 0 ? (
                <button
                  type="button"
                  aria-expanded={panelOpen}
                  aria-controls={panelOpen ? panelId : undefined}
                  onClick={() => {
                    changeOpen(!panelOpen);
                    inputRef.current?.focus();
                  }}
                  className={cn(
                    "touch-target inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
                    panelOpen || active.length > 0
                      ? "text-interaction-text hover:bg-surface-elevated"
                      : "text-secondary hover:bg-surface-elevated hover:text-primary",
                  )}
                >
                  <SlidersHorizontal className="size-4" aria-hidden />
                  <span className="hidden sm:inline">Filter</span>
                  {active.length > 0 ? (
                    <span className="tabular-nums">· {active.length}</span>
                  ) : null}
                </button>
              ) : null}
            </span>
          </div>
        </PopoverAnchor>
        <PopoverContent
          id={panelId}
          align="start"
          className="w-(--radix-popover-trigger-width) max-w-[calc(100vw-2rem)] overflow-hidden p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
          // Choosing with the pointer keeps the caret in the field, so typing carries on.
          onMouseDown={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
          }}
        >
          <CommandList className="max-h-[min(20rem,60vh)] divide-y divide-border px-1.5 py-1">
            {groups.map(({ facet, options }) => (
              <FacetRow
                key={facet.id}
                facet={facet}
                options={options}
                onToggle={(value) => toggle(facet, value)}
              />
            ))}
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
}

function ActiveFilter({
  facet,
  option,
  onRemove,
}: {
  facet: SearchFilterBarFacet;
  option: SearchFilterBarOption;
  onRemove: () => void;
}) {
  const chip = facet.display === "chips" && option.swatchClassName;
  return (
    <span
      className={cn(
        "inline-flex h-7 max-w-full items-center gap-1.5 rounded-full pl-2.5 pr-1 text-xs font-medium",
        chip ? option.swatchClassName : "border border-border bg-surface-elevated text-primary",
      )}
      style={chip ? option.swatchStyle : undefined}
    >
      {!chip && <span className="text-muted">{facet.label}</span>}
      <span className="truncate">{option.label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove filter ${facet.label}: ${option.label}`}
        className="grid size-5 place-items-center rounded-full opacity-70 transition hover:bg-current/15 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        <X className="size-3" aria-hidden />
      </button>
    </span>
  );
}

/**
 * One facet as one line: its name on the left, its values as compact chips with
 * their counts. Tags keep their own colours; other values use the neutral chip
 * and the interaction tint once chosen. Descriptions stay in the tooltip.
 */
function FacetRow({
  facet,
  options,
  onToggle,
}: {
  facet: SearchFilterBarFacet;
  options: readonly SearchFilterBarOption[];
  onToggle: (value: string) => void;
}) {
  return (
    <CommandGroup
      heading={facet.label}
      className={cn(
        "grid gap-x-3 px-1 py-2 sm:grid-cols-[6.5rem_minmax(0,1fr)] sm:items-start",
        "[&_[cmdk-group-heading]]:px-1 [&_[cmdk-group-heading]]:py-1",
        "[&_[cmdk-group-items]]:flex [&_[cmdk-group-items]]:flex-wrap [&_[cmdk-group-items]]:gap-1.5",
      )}
    >
      {options.map((option) => {
        const checked = facet.selected.includes(option.value);
        const tagChip = facet.display === "chips" && option.swatchClassName;
        return (
          <CommandItem
            key={option.value}
            value={`${facet.id}:${option.value}`}
            onSelect={() => onToggle(option.value)}
            aria-checked={checked}
            data-checked={checked}
            title={option.description}
            style={tagChip ? option.swatchStyle : undefined}
            className={cn(
              "inline-flex min-h-7 gap-1.5 rounded-full px-2.5 py-0 text-xs font-medium",
              // The keyboard position is a quiet tint, never a ring that reads as "chosen".
              tagChip
                ? cn(option.swatchClassName, "data-[selected=true]:brightness-95")
                : cn(
                    "border data-[selected=true]:bg-surface-elevated",
                    checked
                      ? "border-focus bg-focus/10 text-interaction-text data-[selected=true]:bg-focus/15"
                      : "border-border bg-surface text-secondary",
                  ),
            )}
          >
            {checked ? <Check className="size-3 shrink-0" strokeWidth={2.5} aria-hidden /> : null}
            {!tagChip && !checked && option.swatchClassName ? (
              <span
                aria-hidden
                className={cn("size-1.5 shrink-0 rounded-full", option.swatchClassName)}
                style={option.swatchStyle}
              />
            ) : null}
            <span className="truncate">{option.label}</span>
            {option.count !== undefined ? (
              <span className="tabular-nums opacity-70">{option.count}</span>
            ) : null}
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}
